import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import userService from '../services/userService';
import { useAuth } from '../context/AuthContext';
import { useWorkspaceTabNavigation } from '../context/WorkspaceTabsContext';
import { ArrowLeftIcon, AlertIcon, CheckCircleIcon } from '../components/Icons';
import { MERCHANT_PROVIDER_IMPORT_PERMISSIONS, isMerchantImportDependency } from '../utils/merchantProviderPermissions';
import './UserDetail.css';

const MODULES = [
    { id: 'dashboard', name: 'Dashboard', group: 'Workspace' },
    { id: 'merchants', name: 'Merchants', group: 'Operations' },
    { id: 'merchant_search', name: 'Merchant Search', group: 'Operations', actions: ['view'] },
    { id: 'finance_reconciliation', name: 'Finance Reconciliation', group: 'Operations' },
    { id: 'tickets', name: 'Ticketing', group: 'Operations' },
    { id: 'master_providers', name: 'Master Providers', group: 'L1 Master' },
    { id: 'payment_gateway_master_providers', name: 'Payment Gateway Providers', group: 'L1 Master' },
    { id: 'countries', name: 'Countries', group: 'Game Management' },
    { id: 'currencies', name: 'Currencies', group: 'Game Management' },
    { id: 'categories', name: 'Categories', group: 'Game Management' },
    { id: 'sub_categories', name: 'Sub Categories', group: 'Game Management' },
    { id: 'sources', name: 'Provider Sources', group: 'Game Management' },
    { id: 'providers', name: 'Providers', group: 'Game Management' },
    { id: 'payment_gateway_categories', name: 'Payment Gateway Categories', group: 'Payment Gateway Management' },
    { id: 'payment_gateway_sources', name: 'Payment Gateway Provider Sources', group: 'Payment Gateway Management' },
    { id: 'payment_gateway_providers', name: 'Payment Gateway Providers', group: 'Payment Gateway Management' },
    { id: 'audit_logs', name: 'Audit Logs', group: 'System' },
    { id: 'users', name: 'Admin Users', group: 'System' }
];

const ACTIONS = [
    { id: 'view', name: 'View' },
    { id: 'create', name: 'Create' },
    { id: 'edit', name: 'Edit' },
    { id: 'delete', name: 'Delete' },
    { id: 'export', name: 'Export' },
    { id: 'import', name: 'Import', modules: ['merchants', 'master_providers'] },
    { id: 'view_financial', name: 'View Financial', modules: ['merchants', 'master_providers', 'payment_gateway_master_providers', 'finance_reconciliation'] },
    { id: 'edit_financial', name: 'Edit Financial', modules: ['merchants', 'master_providers', 'payment_gateway_master_providers', 'finance_reconciliation'] },
    { id: 'view_reporting', name: 'View Reporting', modules: ['master_providers'] },
    { id: 'edit_reporting', name: 'Edit Reporting', modules: ['master_providers'] },
    { id: 'assign', name: 'Assign', modules: ['tickets'] },
    { id: 'comment', name: 'Comment', modules: ['tickets'] },
    { id: 'close', name: 'Close', modules: ['tickets'] }
];

const ROLES = [
    { id: 'super_admin', name: 'Super Admin' },
    { id: 'admin', name: 'Admin' },
    { id: 'finance', name: 'Finance' },
    { id: 'account_manager', name: 'Account Manager' },
    { id: 'developer', name: 'Developer' }
];

const getRoleLabel = (roleId) => ROLES.find(role => role.id === roleId)?.name || roleId;

const isActionAvailable = (moduleId, action) => {
    const moduleActions = MODULES.find(module => module.id === moduleId)?.actions;
    return (
        (!moduleActions || moduleActions.includes(action.id)) &&
        !(moduleId === 'merchants' && action.id === 'delete') &&
        (!action.modules || action.modules.includes(moduleId))
    );
};

const getAvailableActions = (moduleId) => ACTIONS.filter(action => isActionAvailable(moduleId, action));

const countSelectedPermissions = (permissions = {}) => (
    MODULES.reduce((total, module) => (
        total + getAvailableActions(module.id).filter(action => permissions[module.id]?.[action.id]).length
    ), 0)
);

const countEnabledModules = (permissions = {}) => (
    MODULES.filter(module => getAvailableActions(module.id).some(action => permissions[module.id]?.[action.id])).length
);

const getTotalAvailablePermissions = () => MODULES.reduce((total, module) => total + getAvailableActions(module.id).length, 0);

const UserDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { closeCurrentTabAndNavigate } = useWorkspaceTabNavigation();
    const { hasPermission, getDefaultRoute } = useAuth();
    const isEdit = !!id;

    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        role: 'admin',
        is_active: true,
        permissions: {}
    });

    const fetchUser = useCallback(async () => {
        try {
            const res = await userService.getUserById(id);
            if (res.success) {
                const userData = res.data;
                const permissions = userData.permissions || {};
                setFormData({
                    username: userData.username,
                    email: userData.email,
                    password: '',
                    role: userData.role,
                    is_active: userData.is_active,
                    permissions: permissions
                });
            } else {
                setError(res.message);
            }
        } catch (err) {
            setError('Failed to fetch user data');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        // Page level protection
        if (!hasPermission('users', 'view')) {
            navigate(getDefaultRoute(), { replace: true });
            return;
        }

        if (isEdit && !hasPermission('users', 'edit')) {
            navigate('/users');
            return;
        }

        if (!isEdit && !hasPermission('users', 'create')) {
            navigate('/users');
            return;
        }

        if (isEdit) {
            fetchUser();
        }
    }, [isEdit, fetchUser, hasPermission, getDefaultRoute, navigate]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handlePermissionChange = (module, action, checked) => {
        setFormData(prev => {
            const newPermissions = { ...prev.permissions };
            const modulePerms = { ...(newPermissions[module] || {}) };

            if (module === 'merchants' && checked && action === 'import') {
                MERCHANT_PROVIDER_IMPORT_PERMISSIONS.forEach(permission => { modulePerms[permission] = true; });
            }
            if (module === 'merchants' && !checked && modulePerms.import && isMerchantImportDependency(action)) {
                return prev;
            }

            // If checking anything other than view, auto-check view
            if (checked && action !== 'view') {
                modulePerms.view = true;
            }

            if (checked && action === 'edit_financial') {
                modulePerms.view_financial = true;
            }

            if (checked && action === 'edit_reporting') {
                modulePerms.view_reporting = true;
            }

            // If unchecking view, check if other permissions are active
            if (!checked && action === 'view') {
                const hasOtherActive = getAvailableActions(module).some(action => action.id !== 'view' && modulePerms[action.id]);
                if (hasOtherActive) {
                    return prev; // Don't allow unchecking view if others are active
                }
            }

            if (!checked && action === 'view_financial' && modulePerms.edit_financial) {
                return prev;
            }

            if (!checked && action === 'view_reporting' && modulePerms.edit_reporting) {
                return prev;
            }

            modulePerms[action] = checked;
            newPermissions[module] = modulePerms;

            return {
                ...prev,
                permissions: newPermissions
            };
        });
    };

    const handleToggleAllModule = (module, checked) => {
        setFormData(prev => {
            const newPermissions = { ...prev.permissions };
            const modulePerms = {};
            getAvailableActions(module).forEach(action => {
                modulePerms[action.id] = checked;
            });
            newPermissions[module] = modulePerms;
            return {
                ...prev,
                permissions: newPermissions
            };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);

        try {
            const dataToSave = { ...formData };
            if (!dataToSave.password) {
                delete dataToSave.password;
            }

            const res = isEdit
                ? await userService.updateUser(id, dataToSave)
                : await userService.createUser(dataToSave);

            if (res.success) {
                navigate('/users');
            } else {
                setError(res.message);
            }
        } catch (err) {
            setError('An error occurred while saving user');
        } finally {
            setSaving(false);
        }
    };

    const selectedPermissionCount = countSelectedPermissions(formData.permissions);
    const enabledModuleCount = countEnabledModules(formData.permissions);
    const totalPermissionCount = getTotalAvailablePermissions();
    const isSuperAdmin = formData.role === 'super_admin';

    const isModuleFullySelected = (moduleId) => (
        getAvailableActions(moduleId).every(action => formData.permissions[moduleId]?.[action.id])
    );

    const getModulePermissionCount = (moduleId) => (
        getAvailableActions(moduleId).filter(action => formData.permissions[moduleId]?.[action.id]).length
    );

    const isPermissionDisabled = (moduleId, actionId) => (
        isSuperAdmin ||
        (moduleId === 'merchants' && formData.permissions[moduleId]?.import && isMerchantImportDependency(actionId)) ||
        (actionId === 'view' && getAvailableActions(moduleId).some(action => action.id !== 'view' && formData.permissions[moduleId]?.[action.id])) ||
        (actionId === 'view_financial' && formData.permissions[moduleId]?.edit_financial) ||
        (actionId === 'view_reporting' && formData.permissions[moduleId]?.edit_reporting)
    );

    if (loading) {
        return (
            <div className="user-detail-page">
                <div className="user-detail-loading card">Loading user details...</div>
            </div>
        );
    }

    return (
        <div className="user-detail-page">
            <div className="user-detail-header">
                <button className="user-detail-back-btn" type="button" onClick={() => closeCurrentTabAndNavigate('/users')} aria-label="Back to users">
                    <ArrowLeftIcon />
                </button>
                <div className="user-detail-title-block">
                    <span className="user-detail-section-label">Operations</span>
                    <h1>{isEdit ? 'Edit User' : 'Create User'}</h1>
                    <div className="user-detail-subtitle">
                        <span>{formData.email || 'New admin account'}</span>
                        <span>{getRoleLabel(formData.role)}</span>
                    </div>
                </div>
                <div className={`user-detail-status-pill ${formData.is_active ? 'is-active' : 'is-inactive'}`}>
                    <span>{formData.is_active ? 'Active' : 'Inactive'}</span>
                </div>
            </div>

            {error && (
                <div className="user-detail-alert">
                    <AlertIcon size={20} />
                    <span>{error}</span>
                </div>
            )}

            <form onSubmit={handleSubmit}>
                <div className="user-detail-grid">
                    <section className="user-detail-card user-detail-identity-card">
                        <div className="user-detail-card-header">
                            <div>
                                <h2>User Information</h2>
                                <p>Core account details used for login and access routing.</p>
                            </div>
                        </div>
                        <div className="user-detail-fields">
                            <label className="user-detail-field">
                                <span>Username *</span>
                                <input type="text" name="username" className="input" value={formData.username} onChange={handleChange} required placeholder="e.g. john_doe" />
                            </label>
                            <label className="user-detail-field">
                                <span>Email Address *</span>
                                <input type="email" name="email" className="input" value={formData.email} onChange={handleChange} required placeholder="e.g. john@example.com" />
                            </label>
                            <label className="user-detail-field">
                                <span>{isEdit ? 'Change Password' : 'Password *'}</span>
                                <input type="password" name="password" className="input" value={formData.password} onChange={handleChange} required={!isEdit} placeholder={isEdit ? 'Leave blank to keep current' : ''} />
                            </label>
                            <label className="user-detail-field">
                                <span>Role</span>
                                <select name="role" className="input" value={formData.role} onChange={handleChange}>
                                    {ROLES.map(role => (
                                        <option key={role.id} value={role.id}>{role.name}</option>
                                    ))}
                                </select>
                            </label>
                        </div>
                    </section>

                    <aside className="user-detail-card user-detail-access-card">
                        <div className="user-detail-card-header compact">
                            <div>
                                <h2>Access State</h2>
                                <p>Quick status and permission coverage.</p>
                            </div>
                        </div>
                        <div className="user-detail-status-control">
                            <div>
                                <span>Account Status</span>
                                <strong>{formData.is_active ? 'Enabled' : 'Disabled'}</strong>
                            </div>
                            <label className="switch">
                                <input type="checkbox" name="is_active" checked={formData.is_active} onChange={handleChange} />
                                <span className="slider"></span>
                            </label>
                        </div>
                        <div className="user-detail-stats">
                            <div>
                                <span>Modules</span>
                                <strong>{enabledModuleCount}/{MODULES.length}</strong>
                            </div>
                            <div>
                                <span>Permissions</span>
                                <strong>{isSuperAdmin ? 'Full access' : `${selectedPermissionCount}/${totalPermissionCount}`}</strong>
                            </div>
                        </div>
                        {isSuperAdmin && (
                            <div className="user-detail-super-note">
                                <CheckCircleIcon size={16} />
                                <span>Super Admin has full access. Individual permissions are ignored.</span>
                            </div>
                        )}
                    </aside>
                </div>

                <section className="user-detail-card user-detail-permissions-card">
                    <div className="user-detail-permissions-header">
                        <div>
                            <h2>Permissions</h2>
                            <p>{isSuperAdmin ? 'This role bypasses granular checks.' : 'Grant module access and related actions from one matrix.'}</p>
                        </div>
                        <div className="user-detail-permission-summary">
                            <span>{enabledModuleCount} modules</span>
                            <strong>{isSuperAdmin ? 'Full access' : `${selectedPermissionCount} selected`}</strong>
                        </div>
                    </div>

                    <div className="permission-matrix-wrap">
                        <table className="permission-matrix">
                            <thead>
                                <tr>
                                    <th className="permission-module-col">Module</th>
                                    {ACTIONS.map(action => (
                                        <th key={action.id}>{action.name}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {MODULES.map((module, idx) => {
                                    const showGroup = idx === 0 || MODULES[idx - 1].group !== module.group;
                                    const modulePermissionCount = getModulePermissionCount(module.id);
                                    const availableActionCount = getAvailableActions(module.id).length;

                                    return (
                                        <React.Fragment key={module.id}>
                                            {showGroup && (
                                                <tr className="permission-group-row">
                                                    <td colSpan={ACTIONS.length + 1}>{module.group}</td>
                                                </tr>
                                            )}
                                            <tr>
                                                <td className="permission-module-col">
                                                    <div className="permission-module-cell">
                                                        <input
                                                            type="checkbox"
                                                            className="permission-checkbox"
                                                            disabled={formData.role === 'super_admin'}
                                                            checked={isModuleFullySelected(module.id)}
                                                            onChange={(e) => handleToggleAllModule(module.id, e.target.checked)}
                                                        />
                                                        <div>
                                                            <span>{module.name}</span>
                                                            <small>{modulePermissionCount}/{availableActionCount} enabled</small>
                                                        </div>
                                                    </div>
                                                </td>
                                                {ACTIONS.map(action => (
                                                    <td key={action.id}>
                                                        {!isActionAvailable(module.id, action) ? (
                                                            <span className="permission-unavailable">-</span>
                                                        ) : (
                                                            <input
                                                                type="checkbox"
                                                                className="permission-checkbox"
                                                                disabled={isPermissionDisabled(module.id, action.id)}
                                                                checked={formData.permissions[module.id]?.[action.id] || false}
                                                                onChange={(e) => handlePermissionChange(module.id, action.id, e.target.checked)}
                                                                aria-label={`${module.name} ${action.name}`}
                                                            />
                                                        )}
                                                    </td>
                                                ))}
                                            </tr>
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </section>

                <div className="user-detail-actions">
                    <button type="button" className="btn btn-secondary" onClick={() => closeCurrentTabAndNavigate('/users')}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={saving}>
                        {saving ? 'Saving...' : (isEdit ? 'Update User' : 'Create User')}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default UserDetail;
